import keyBy from "lodash/keyBy";
import uniq from "lodash/uniq";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { utc } from "PFCore/helpers/date";
import { BookingWithData } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useMemo } from "react";

import { useSplitModeState } from "../booking_edit_mode_store";
import { operationsFromOperationsHistory, splitBooking } from "../utils";
import { useDraftOperation } from "./use_draft_operation";

export type UseSplitDraftOperationReturn = {
  performSplitDraftOperation: (bookings: BookingWithData[]) => BookingWithData[];
};

export const useSplitDraftOperation = (): UseSplitDraftOperationReturn => {
  const { operationsHistory } = useSplitModeState();
  const { formatISODate } = useDateFormatter();

  const operations = useMemo(() => operationsFromOperationsHistory(operationsHistory), [operationsHistory]);
  const affectedBookingIds = useMemo(() => uniq(operations.map(({ bookingId }) => bookingId)), [operations]);
  const splitDatesByBookingId = useMemo(() => keyBy(operations, ({ bookingId }) => bookingId), [operations]);

  const transformer = useMemo(
    () => (booking: BookingWithData) => {
      if (!booking.id) {
        return booking;
      }

      const splitActions = splitDatesByBookingId[booking.id]?.splitDates;
      return splitBooking(booking, splitActions ?? [], utc, formatISODate);
    },
    [splitDatesByBookingId, formatISODate]
  );

  const performSplitDraftOperation = useDraftOperation(EditMode.Split, affectedBookingIds, transformer);

  return {
    performSplitDraftOperation
  };
};
